import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Lock } from "lucide-react";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ProviderLogo } from "@/components/ProviderCard";

export default function ClaimListing() {
  const { slug } = useParams();
  const [provider, setProvider] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [blocker, setBlocker] = useState(null); // {code, ...} when backend says we can't claim
  const [form, setForm] = useState({
    requester_name: "", requester_email: "", job_title: "", company: "",
    linkedin_url: "", message: "",
  });

  useEffect(() => {
    api.get(`/providers/slug/${slug}`).then(({ data }) => setProvider(data.provider)).catch(() => {});
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!provider) return;
    if (!consent) {
      toast.error("Please confirm you are authorised to manage this listing.");
      return;
    }
    try {
      // Pre-flight check — gives a smooth experience without round-tripping the full form.
      const { data: pre } = await api.post("/claims/check-eligibility", { provider_id: provider.id, email: form.requester_email });
      if (pre.code !== "can_submit_claim") {
        setBlocker(pre);
        return;
      }
      await api.post("/claims", { provider_id: provider.id, ...form });
      api.post("/analytics/event", { event_type: "claim_submitted", provider_id: provider.id }).catch(()=>{});
      setSubmitted(true);
      toast.success("Claim request submitted.");
    } catch (err) {
      // Backend conflict (409) carries the same structured detail.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && detail.code) {
        setBlocker(detail);
        return;
      }
      toast.error(formatApiError(err));
    }
  };

  if (!provider) return <div className="max-w-3xl mx-auto px-4 py-20 text-slate-500">Loading…</div>;

  if (blocker) {
    return <ClaimBlockerScreen blocker={blocker} email={form.requester_email} slug={slug} onBack={() => setBlocker(null)}/>;
  }
  if (submitted) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <CheckCircle2 size={56} className="mx-auto text-emerald-500"/>
      <h1 className="font-display font-extrabold text-3xl mt-4">Claim request submitted</h1>
      <p className="mt-3 text-slate-600 max-w-xl mx-auto leading-relaxed">
        Thanks. Strategic Tracking will review your request before giving access to the listing.
        Claim approval does not automatically activate verification, featured placement or direct lead routing.
      </p>
      <p className="mt-2 text-sm text-slate-500">We'll email <span className="font-mono">{form.requester_email}</span> when a decision is made.</p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <Link to="/solutions" className="btn-primary" data-testid="claim-back-directory">Back to directory</Link>
        <Link to={`/solutions/${slug}`} className="btn-secondary">Back to listing</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="card-elevated p-8">
        <div className="flex items-start gap-4">
          <ProviderLogo provider={provider} size={56}/>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Claim listing</div>
            <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">{provider.name}</h1>
            <p className="text-sm text-slate-500 mt-1">{provider.website}</p>
          </div>
        </div>

        <div className="mt-6 rounded-md bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900 inline-flex items-start gap-2">
          <ShieldCheck size={16} className="mt-0.5"/>
          <div>
            <strong>Why claim?</strong> Verify your details, route leads directly to your team,
            unlock provider analytics and upgrade to a Verified or Featured listing.
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="claim-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name *">
              <Input value={form.requester_name} onChange={(e)=>setForm({...form, requester_name: e.target.value})} required data-testid="claim-name"/>
            </Field>
            <Field label="Work email *">
              <Input type="email" value={form.requester_email} onChange={(e)=>setForm({...form, requester_email: e.target.value})} required data-testid="claim-email"/>
            </Field>
            <Field label="Job title">
              <Input value={form.job_title} onChange={(e)=>setForm({...form, job_title: e.target.value})} data-testid="claim-job"/>
            </Field>
            <Field label="Company">
              <Input value={form.company} onChange={(e)=>setForm({...form, company: e.target.value})} placeholder={provider.name} data-testid="claim-company"/>
            </Field>
          </div>
          <Field label="Reason for claim">
            <Textarea rows={4} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} placeholder="Briefly confirm your role and authority to manage this listing." data-testid="claim-message"/>
          </Field>
          <Field label="LinkedIn profile (optional)">
            <Input value={form.linkedin_url} onChange={(e)=>setForm({...form, linkedin_url: e.target.value})} placeholder="https://www.linkedin.com/in/…" data-testid="claim-linkedin"/>
          </Field>
          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e)=>setConsent(e.target.checked)}
              className="mt-0.5 rounded border-slate-300"
              required
              data-testid="claim-consent"
            />
            <span>I confirm I am authorised to request access to manage this listing.</span>
          </label>
          <p className="text-xs text-slate-500">
            Tip: use your company email matching <span className="font-mono">{provider.website}</span> to speed up verification.
            Claim approval is manual and does not activate Verified, Featured or direct lead routing.
          </p>
          <button type="submit" className="btn-primary w-full justify-center h-11" data-testid="claim-submit">Submit claim request</button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}


function ClaimBlockerScreen({ blocker, email, slug, onBack }) {
  const COPY = {
    already_has_access: {
      title: "You already have access to this listing",
      body: "This email is already linked to this listing. Please log in to manage your company profile.",
      primary: { label: "Log in", href: `/login?email=${encodeURIComponent(email)}` },
      secondary: { label: "Reset password", href: `/forgot-password?email=${encodeURIComponent(email)}` },
    },
    listing_already_claimed: {
      title: "This listing has already been claimed",
      body: "If you believe you should have access, please contact Strategic Tracking support.",
      primary: { label: "Log in", href: "/login" },
      secondary: { label: "Contact support", href: "mailto:matthew@strategictracking.com?subject=Claim%20support%20request" },
    },
    existing_provider_account: {
      title: "This email already has a provider account",
      body: "Please log in first, then request access to additional listings from your dashboard or contact Strategic Tracking.",
      primary: { label: "Log in", href: `/login?email=${encodeURIComponent(email)}` },
      secondary: { label: "Contact support", href: "mailto:matthew@strategictracking.com?subject=Additional%20listing%20access" },
    },
    pending_approval: {
      title: "An account already exists for this email",
      body: "Provider access has not yet been approved. Please log in to check your status or contact Strategic Tracking.",
      primary: { label: "Log in", href: `/login?email=${encodeURIComponent(email)}` },
      secondary: { label: "Contact support", href: "mailto:matthew@strategictracking.com?subject=Pending%20provider%20approval" },
    },
    existing_pending_claim: {
      title: "You already have a claim request pending",
      body: "Strategic Tracking will review your existing claim and contact you. We won’t create a second one.",
      primary: { label: "Back to listing", href: `/solutions/${slug}` },
      secondary: { label: "Log in", href: `/login?email=${encodeURIComponent(email)}` },
    },
  };

  const c = COPY[blocker.code] || COPY.existing_pending_claim;
  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid={`claim-blocker-${blocker.code}`}>
      <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 grid place-items-center mx-auto">
        <Lock size={20}/>
      </div>
      <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-5">{c.title}</h1>
      <p className="mt-3 text-slate-600 leading-relaxed">{c.body}</p>
      <p className="mt-2 text-sm text-slate-500">For <span className="font-mono">{email}</span></p>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <Link to={c.primary.href} className="btn-primary" data-testid="blocker-primary">{c.primary.label}</Link>
        <Link to={c.secondary.href} className="btn-secondary" data-testid="blocker-secondary">{c.secondary.label}</Link>
        <button type="button" onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 underline" data-testid="blocker-back">Use a different email</button>
      </div>
    </div>
  );
}
